import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { buildClassShareUrl } from '@/features/class/manage/classShareUtils';
import type { ManagedClass } from '@/features/class/manage/types';
import { MOCK_CURRENT_USER_PROFILE } from '@/features/profile/mockData';
import Modal from '@/shared/ui/Modal';
import ModalHeader from '@/shared/ui/ModalHeader';
import Button from '@/shared/ui/Button';
import Avatar from '@/shared/ui/Avatar';
import { CopyIcon } from '@/shared/icons';
import { getInitials } from '@/lib/getInitials';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/shared/lib/cn';
import { textStyles } from '@/shared/tokens/typography';

interface ClassShareModalProps {
  classItem: ManagedClass;
  onClose: () => void;
}

const SHARE_AVATAR_CLASS =
  'h-24 w-24 rounded-full border-4 border-white object-cover shadow-sm ring-1 ring-gray-200';

export default function ClassShareModal({ classItem, onClose }: ClassShareModalProps) {
  const { t } = useTranslation('class');
  const { t: tCommon } = useTranslation('common');
  const authUser = useAuthStore((state) => state.user);
  const [linkCopied, setLinkCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  const ownerInitials = authUser ? getInitials(authUser.name) : MOCK_CURRENT_USER_PROFILE.initials;
  const ownerAvatarUrl = authUser?.avatarUrl ?? MOCK_CURRENT_USER_PROFILE.avatarUrl;

  const shareUrl = buildClassShareUrl(classItem.code);

  const flashCopied = useCallback((setter: (value: boolean) => void) => {
    setter(true);
    window.setTimeout(() => setter(false), 2000);
  }, []);

  const handleCopyCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(classItem.code);
      flashCopied(setCodeCopied);
    } catch {
      setCodeCopied(false);
    }
  }, [classItem.code, flashCopied]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      flashCopied(setLinkCopied);
    } catch {
      setLinkCopied(false);
    }
  }, [flashCopied, shareUrl]);

  return (
    <Modal
      onClose={onClose}
      zIndexClassName="z-[100]"
      className="flex w-full max-w-lg flex-col"
      ariaLabel={t('shareTitle')}
    >
      <ModalHeader title={t('shareTitle')} onClose={onClose} />

      <div className="px-4 py-4 sm:px-6">
        <div className="rounded-xl border border-gray-300 bg-gray-50/80 p-6">
          <div className="flex flex-col items-center text-center">
            {ownerAvatarUrl ? (
              <img src={ownerAvatarUrl} alt="" className={SHARE_AVATAR_CLASS} />
            ) : (
              <Avatar
                initials={ownerInitials}
                size="xl"
                className={cn(SHARE_AVATAR_CLASS, 'bg-gray-100 text-gray-600')}
              />
            )}

            <p className={cn(textStyles.cardTitle, 'mt-4 text-base')}>{classItem.name}</p>

            <p className={cn(textStyles.caption, 'mt-1 tabular-nums')}>{classItem.code}</p>

            <div
              className="mt-6 rounded-xl border border-gray-300 bg-white p-4 shadow-sm"
              role="img"
              aria-label={t('shareQrAria', { className: classItem.name })}
            >
              <QRCode
                value={shareUrl}
                size={200}
                level="M"
                fgColor="#000000"
                bgColor="#FFFFFF"
                className="mx-auto h-auto max-w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid shrink-0 grid-cols-1 gap-2 border-t border-gray-300 px-4 py-3 sm:grid-cols-2 sm:px-6">
        <Button
          type="button"
          variant="primary"
          size="md"
          fullWidth
          onClick={() => void handleCopyLink()}
          leftIcon={<CopyIcon className="h-4 w-4" />}
        >
          {linkCopied ? tCommon('linkCopied') : t('shareCopyLink')}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          fullWidth
          onClick={() => void handleCopyCode()}
          leftIcon={<CopyIcon className="h-4 w-4" />}
        >
          {codeCopied ? t('shareCodeCopied') : t('shareCopyCode')}
        </Button>
      </div>
    </Modal>
  );
}
